import { useEffect, useRef } from "react";

const INTERACTIVE = 'a, button, [role="button"], [role="tab"], summary, label[for], [data-cursor="grow"]';
const TEXT_INPUT = 'input:not([type="checkbox"]):not([type="radio"]), textarea, select, [contenteditable="true"]';

/**
 * A soft ring that trails the (still visible) cursor and opens up over
 * anything clickable. Desktop / fine pointers only; off for reduced motion.
 * Purely decorative: pointer-events none, aria-hidden.
 */
export function CursorHalo() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const fine = window.matchMedia("(hover: hover) and (pointer: fine)");
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)");
    if (!fine.matches || reduced.matches) return;

    let x = -100;
    let y = -100;
    let cx = x;
    let cy = y;
    let frame = 0;
    let shown = false;

    const tick = () => {
      // Ease towards the pointer for a calm, weighted follow
      cx += (x - cx) * 0.18;
      cy += (y - cy) * 0.18;
      el.style.transform = `translate3d(${cx}px, ${cy}px, 0)`;
      frame = Math.abs(x - cx) + Math.abs(y - cy) > 0.3 ? requestAnimationFrame(tick) : 0;
    };

    const onMove = (e: PointerEvent) => {
      if (e.pointerType !== "mouse") return;
      x = e.clientX;
      y = e.clientY;
      if (!shown) {
        shown = true;
        cx = x;
        cy = y;
        el.dataset.visible = "true";
      }
      const target = e.target instanceof Element ? e.target : null;
      const mode = target?.closest(TEXT_INPUT) ? "hidden" : target?.closest(INTERACTIVE) ? "grow" : "idle";
      if (el.dataset.mode !== mode) el.dataset.mode = mode;
      if (!frame) frame = requestAnimationFrame(tick);
    };
    const onLeave = () => {
      shown = false;
      el.dataset.visible = "false";
    };
    const onDown = () => (el.dataset.pressed = "true");
    const onUp = () => (el.dataset.pressed = "false");

    window.addEventListener("pointermove", onMove, { passive: true });
    window.addEventListener("pointerdown", onDown, { passive: true });
    window.addEventListener("pointerup", onUp, { passive: true });
    document.documentElement.addEventListener("pointerleave", onLeave);
    return () => {
      if (frame) cancelAnimationFrame(frame);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerdown", onDown);
      window.removeEventListener("pointerup", onUp);
      document.documentElement.removeEventListener("pointerleave", onLeave);
    };
  }, []);

  return (
    <div ref={ref} aria-hidden="true" className="op-cursor" data-visible="false" data-mode="idle">
      <span />
    </div>
  );
}
